import Images from 'app/assets/img';
import React, { memo, useMemo, useState } from 'react';
import styled from 'styled-components';
import TooltipParagraph from '../Catalog/tooltipParagraph';
import { selectTranslations } from '../App/slice/selectors';
import { useSelector } from 'react-redux';
import { formatNumber } from 'utils/helper';
import StyledButton from 'app/components/StyledButton';
import ReadMore from './ReadMore';
import DialogAllTickets from './components/DialogAllTickets';
import { getSelectedFlights } from 'utils/sessionStorageHelper';
import { COMBO_HOLIDAY_TYPES } from '../BookingMask';
interface IHolidayFHTProps {
  onSelectCombo: (data: any) => void;
  onSelectOtherPackage: (data: any) => void;
  data?: any;
  flightInfo?: any;
  comboType?: any;
}
const FALLBACK_IMAGE = Images.ImgBanner;
const HolidayListFHT = (props: IHolidayFHTProps) => {
  const {
    onSelectCombo,
    onSelectOtherPackage,
    data = [],
    flightInfo,
    comboType,
  } = props;
  const sessionFlights = getSelectedFlights();
  const isFH = comboType === COMBO_HOLIDAY_TYPES.FLIGHT_HOTEL;
  const [isShowAllTicket, setIsShowAllTicket] = useState<boolean>(false);
  const [ticketData, setTicketData] = useState<any>([]);
  const translations = useSelector(selectTranslations);
  const paxAdditionalAmountFlightSession = sessionFlights?.reduce((sum, f) => {
    return sum + Number(f?.pricing?.paxAdditionalAmount || 0);
  }, 0);
  const paxAdditionalAmountFlightInfo = flightInfo?.listFlight?.reduce(
    (sum, f) => {
      return (
        sum +
        Number(f?.listTrip?.[0]?.listPricing?.[0]?.paxAdditionalAmount || 0)
      );
    },
    0,
  );
  const normalizedData = useMemo(() => {
    return data.map(item => {
      const firstRoom = item?.roomTypes?.[0];
      const roomType = firstRoom?.roomType;
      const pricing = firstRoom?.pricing;
      const tickets = pricing?.tickets || [];
      const firstTicket = tickets?.[0];
      // hotels.roomTypes.roomType.pricing.paxPrice + roomTypes.roomType.pricing.tickets.paxAdditionalAmount + data.listTrip.cabin.paxAdditionalAmount
      const paxPrice = Number(pricing?.paxPrice || 0);
      const paxAdditionalAmountTicket = Number(
        tickets?.[0]?.paxAdditionalAmount || 0,
      );
      const paxAdditionalAmountFlight = Number(
        paxAdditionalAmountFlightSession || paxAdditionalAmountFlightInfo || 0,
      );
      const ticketLength = tickets?.length ?? 0;
      const isIncludeAllTicket = pricing?.isIncludeAllTicket;
      const isShowViewAllTicket = ticketLength > 1 && isIncludeAllTicket;
      const ticketRemain = Math.max(ticketLength - 1, 0);
      return {
        raw: item,
        hotelId: item?.hotel?.id,
        hotelName: item?.hotel?.name || '',
        address: item?.hotel?.address || '',
        hotelImage: roomType?.images?.[0]?.url || FALLBACK_IMAGE,
        roomName: roomType?.name || '',
        roomSize: roomType?.roomSize,
        viewType: roomType?.viewType || '',
        amenities: roomType?.amenities || [],
        isShowViewAllTicket,
        ticketRemain,
        tickets,
        ticketName: firstTicket?.name || '',
        ticketDescription: firstTicket?.description || '',
        ticketImage: firstTicket?.media?.[0] || FALLBACK_IMAGE,
        price: paxPrice + paxAdditionalAmountTicket + paxAdditionalAmountFlight,
      };
    });
  }, [data]);
  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    e.currentTarget.onerror = null;
    e.currentTarget.src = FALLBACK_IMAGE;
  };
  const onShowAllTicket = arrTickets => {
    setTicketData(arrTickets);
    setIsShowAllTicket(true);
  };
  const onCloseDialog = () => {
    setTicketData([]);
    setIsShowAllTicket(false);
  };
  return (
    <Wrapper>
      {normalizedData.map(item => (
        <Card key={item.hotelId}>
          <HotelName ellipsis={{ rows: 2 }}>{item.hotelName}</HotelName>
          <Location>{item.address}</Location>
          <Section>
            <TopSection $isFH={isFH}>
              <RoomBox $isFH={isFH}>
                <Thumb
                  src={item.hotelImage}
                  alt={item.hotelName}
                  onError={handleImageError}
                />
                <Content>
                  <Title ellipsis={{ rows: 1 }}>{item.roomName}</Title>
                  <Desc ellipsis={{ rows: 1 }}>
                    {[item.roomSize && `${item.roomSize}m2`, item.viewType]
                      .filter(Boolean)
                      .join(', ')}
                  </Desc>
                </Content>
              </RoomBox>
              {!isFH && (
                <ComboBox>
                  <Thumb
                    src={item.ticketImage}
                    alt={item.ticketName}
                    onError={handleImageError}
                  />
                  <Content>
                    <Title ellipsis={{ rows: 2 }}>
                      {item.ticketName}
                      {item.isShowViewAllTicket && ` + ${item.ticketRemain}`}
                    </Title>
                    <ReadMore lines={1} html={item.ticketDescription} />
                    {item.isShowViewAllTicket && (
                      <ButtonSeeAllTicket
                        onClick={() => onShowAllTicket(item.tickets)}
                      >
                        {translations?.['b2b.txt.viewAllTickets']} &gt;
                      </ButtonSeeAllTicket>
                    )}
                  </Content>
                </ComboBox>
              )}
            </TopSection>
            <BottomSection>
              <TagGroup>
                {item.amenities.map((tag: any) => (
                  <Tag key={tag?.id || tag?.code || tag?.name}>{tag?.name}</Tag>
                ))}
              </TagGroup>
              <PriceWrapper>
                <PriceLeft>
                  <PriceLabel>
                    {translations?.['b2b.txt.comboPricePerPerson']}
                  </PriceLabel>
                  <Price>{formatNumber(item.price, '.')}đ</Price>
                </PriceLeft>
                <StyledButton
                  textColor="#fff"
                  style={{
                    flex: 1,
                    maxWidth: 'fit-content',
                    borderRadius: 8,
                  }}
                  height="40px"
                  onClick={() => onSelectCombo(item.raw)}
                >
                  {translations?.['b2b.txt.select']}
                </StyledButton>
              </PriceWrapper>
            </BottomSection>
          </Section>
          <Footer>
            <FooterText>
              {translations?.['b2b.txt.comboPackageIncludes']}
            </FooterText>
            <MoreButton onClick={() => onSelectOtherPackage(item.raw)}>
              {translations?.['b2b.txt.viewMorePackageOptions']} &gt;
            </MoreButton>
          </Footer>
        </Card>
      ))}
      {isShowAllTicket && (
        <DialogAllTickets
          tickets={ticketData}
          isShow={isShowAllTicket}
          onCloseDialog={onCloseDialog}
        />
      )}
    </Wrapper>
  );
};
export default memo(HolidayListFHT);
const Wrapper = styled.div``;
const Card = styled.div`
  background: #fff;
  margin-bottom: 10px;
  border: 1px solid #e5e5e5;
  border-radius: 12px;
  padding: 14px;
`;
const HotelName = styled(TooltipParagraph)`
  font-size: 18px;
  font-weight: 600;
  color: #000;
  margin-bottom: 6px;
`;
const Location = styled(TooltipParagraph)`
  font-size: 14px;
  color: #777;
  margin-bottom: 16px;
`;
const Section = styled.div`
  background: #f8f8f8;
  border-radius: 12px;
`;
const TopSection = styled.div<{ $isFH?: boolean }>`
  display: grid;
  grid-template-columns: ${props =>
    props.$isFH ? '1fr' : `repeat(2, minmax(0, 1fr))`};
  background: #fff;
  border-radius: 12px;
  border: 1px solid #e5e5e5;
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const RoomBox = styled.div<{ $isFH?: boolean }>`
  display: flex;
  gap: 12px;
  padding: 12px;
  border-right: ${props => (props.$isFH ? 'none' : `1px solid #e5e5e5`)};
  @media (max-width: 992px) {
    border-right: none;
    border-bottom: 1px solid #e5e5e5;
  }
`;

const ComboBox = styled.div`
  display: flex;
  gap: 12px;
  padding: 12px;
`;

const Thumb = styled.img`
  min-width: 68px;
  width: 68px;
  height: 68px;
  border-radius: 12px;
  object-fit: cover;
`;

const Content = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const Title = styled(TooltipParagraph)`
  font-weight: 600;
  font-size: 14px;
  margin-bottom: 4px !important;
  max-width: 95%;
`;

const Desc = styled(TooltipParagraph)`
  font-size: 12px;
  color: #666;
  max-width: 95%;
`;
const ButtonSeeAllTicket = styled.button`
  background: transparent;
  border: none;
  padding: 0px;
  margin-top: auto;
  max-width: fit-content;
  font-weight: 500;
  color: black;
  &:hover {
    opacity: 0.9;
  }
`;
const BottomSection = styled.div`
  margin-top: 14px;
  display: flex;
  align-items: flex-start;
  @media (max-width: 768px) {
    flex-direction: column;
  }
  padding-bottom: 12px;
  padding-right: 12px;
`;

const TagGroup = styled.div`
  flex: 1;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  padding-left: 12px;
  padding-right: 12px;
  max-width: 75%;
  @media (max-width: 768px) {
    max-width: 100%;
  }
`;
const Tag = styled.div`
  padding: 3px 8px;
  border-radius: 4px;
  background: #d4d4d4;
  font-size: 12px;
  color: #444444;
`;
const PriceWrapper = styled.div`
  margin-left: auto;
  display: flex;
  gap: 12px;
  @media (max-width: 768px) {
    margin-top: 12px;
  }
`;
const PriceLeft = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;
const PriceLabel = styled.div`
  font-size: 12px;
`;
const Price = styled.div`
  font-size: 22px;
  font-weight: 600;
`;
const Footer = styled.div`
  margin-top: 14px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
`;
const FooterText = styled.div`
  font-size: 12px;
  color: #999999;
`;
const MoreButton = styled.button`
  border: none;
  background: transparent;
  padding: 0px;
  color: #f06f24;
  font-weight: 500;
  cursor: pointer;
`;
